import React, { useRef } from "react";
import { DateUtil, I8N, LocaleUtil, StringUtil } from "@eres/core";
import { IPV, IPVTypes, PropertyValuation, TabuService } from "@my-dld/utils/TabuServices";
import Strings from "@my-dld/assets/strings";
import { extractPropInfo } from "../MyDLD/PropertyDetails";
import { AssetCard } from "@10x/dubai-rest-api/dist/property_registry/entity";
import BaseTabuRequestForm, {
	addAgentParticipantId,
	BaseTabuRequestProps,
	Lookup,
	RenderTabuRequiredDocuments,
	ReqTabuDoc,
	TabuDoc,
	TabuLookupName
} from "./BaseTabuRequestForm";
import TextInputField from "@my-dld/components/Form/TextInputField";
import { phoneNumber, toMobileX } from "../MyDLD/MyProfile";
import PickerInput from "@my-dld/components/Form/PickerInput";
import { getLookup, getPropertyEvaluationInfo, saveInternalPropertyEvaluation, savePropertyEvaluation } from "@10x/tabu-mobile-api/dist/requests/api";
import { TabuUtils } from "@10x/tabu-mobile-api";
import { TabuPropertyType } from "@my-dld/utils/PropertyUtils";
import {
	PropertyEvaluationInfo,
	PropertyEvaluationInfoRequest,
	PropertyEvaluationRequest,
	TabuDocumentRequest,
	TabuProcedure
} from "@10x/tabu-mobile-api/dist/requests/entity";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { Redirect } from "react-router";
import { DashboardBCItem } from "@my-dld/contexts/PageContext";
import PageTitle from "@my-dld/components/PageTitle";
import PageContent from "@my-dld/components/PageContent";
import JqDatePicker from "@my-dld/components/JQDatePicker";
import CheckBoxField from "@my-dld/components/Form/CheckBoxField";
import BigNumber from "bignumber.js";
import { getParticipantID } from "@10x/sso";
import CurrencyInput, { CursorPositioner } from "@my-dld/components/Form/CurrencyInput";
import NumberConverter from "@my-dld/components/Form/Converters/NumberConverter";
import NumberDecimalConverter from "@my-dld/components/Form/Converters/NumberDecimalConverter";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import { FixedMobileInput } from "@my-dld/components/FixedEidInput";
import moment from "moment";
import { getSiteLocaleRootUrl } from "@my-dld/utils/UrlUtils";
import Popup from "../../components/Popup/Popup";
import { TabuRequestContext } from "@my-dld/contexts/TabuRequestContext";

interface State {
	requireAccept: boolean;
	accepted: boolean;
	property?: AssetCard;
	mobile: string;
	valuationTypes?: Lookup[];
	valuationType?: Lookup;
	showIncome: boolean;
	income: string;
	expense: string;
	netIncome: string;
	incomeDisable: boolean;
	landUsage: string;
	mapIssueDate?: Date;
	dmNum: string;
	dmNumDisable: boolean;
	landReqDocuments: ReqTabuDoc[];

	locOnFloor: string;
	propertyAge: string;
	propertyView: string;
	mapException: boolean;
	mapExceptionReasons?: Lookup[];
	mapExceptionReason?: Lookup;

	builtUpArea: string;
	builtUpDisable: boolean;
	noOfFloors: string;
	noOfRooms: string;
	insuranceValue: string;
	swimmingPool: boolean;
	service: TabuService;
	isInternal: boolean;
}

interface Props extends BaseTabuRequestProps {
	isInternal?: boolean;
}

class PropertyValuationForm extends BaseTabuRequestForm<Props, State> {
	static contextType = TabuRequestContext;
	private static EVAL_TYPE_DUBAI_MUNICIPALITY = 7;
	context!: React.ContextType<typeof TabuRequestContext>;
	private evaluationInfo: PropertyEvaluationInfo | undefined;

	constructor( props: Props ) {
		super( props );
		let mobile = phoneNumber();
		if ( mobile && !StringUtil.isEmptyStringOrNull( mobile ) ) {
			mobile = toMobileX( mobile );
		}
		const internal = this.props.isInternal;
		this.state = {
			mobile: mobile,
			showIncome: true,
			income: "",
			expense: "",
			netIncome: "",
			incomeDisable: false,
			landUsage: "",
			dmNum: "",
			dmNumDisable: false,
			landReqDocuments: [],
			locOnFloor: "",
			propertyAge: "",
			propertyView: "",
			mapException: false,
			builtUpArea: "",
			builtUpDisable: false,
			noOfFloors: "",
			noOfRooms: "",
			insuranceValue: "",
			swimmingPool: false,
			service: internal ? IPV : PropertyValuation,
			isInternal: internal === true,
			valuationType: internal ? IPVTypes[ 0 ] : undefined,
			requireAccept: false,
			accepted: false,
		};
	}

	static computeNetIncome( income: string, expense: string ): string {
		if ( income == undefined )
			income = "";
		if ( expense == undefined )
			expense = "";
		let iVal = new BigNumber( income.replace( ",", "" ) );
		let eVal = new BigNumber( expense.replace( ",", "" ) );
		return iVal.minus( eVal ).toString();
	}

	componentDidMount() {
		const property = this.context.property;
		this.setState( {
			property: property,
			requireAccept: property?.propertyTypeId === TabuPropertyType.LAND,
			accepted: property?.propertyTypeId !== TabuPropertyType.LAND,
		}, () => {
			if ( property )
				this.loadLookups();
		} );
	}

	render() {
		const property = this.context.property;
		if ( property == null ) {
			return <Redirect to={ "/mydld" }/>;
		}
		const state = this.state;
		const { isInternal, service, accepted, requireAccept } = state;
		const title = LocaleUtil.getLocalValue( service.Name );
		const { val, lbl } = extractPropInfo( property );
		return <PageContent
			title={ `${ title } - ${ lbl } : ${ val }` }
			hideQLinks={ true }>
			<PageTitle title={ title } paths={ [ DashboardBCItem ] }/>
			<div className="card">
				<div className="card-header">
					<span>{ LocaleUtil.getLocalValue( service.Name ) }</span>
				</div>
				<div className="card-body">
					<div className="mandatory-field-info">
						<small>{ Strings.mandatory_fields_note }</small>
					</div>
					{ requireAccept && <>
						<LandConfirmation/>
						<div className="my-4 checkbox-font14">
							<CheckBoxField
								id={ "accepted" }
								holder={ this }
								label={ Strings.land_evaluation_accept_no_building_empty_land }
								labelClassName={ "custom-control-label" }
								required={ true }/>
						</div>
					</> }
					{ ( requireAccept && !accepted ) && <>
						<div className="alert-grey">
							<i className="fas fa-info-circle"/>
							{ Strings.land_evaluation_accept_no_building_empty_land_info }
							<a href="" className="btn-link" onClick={ this.openPT }>
								{ Strings.locate_property_trustee_office }
								<i className="fas fa-angle-right"/>
							</a>
						</div>
					</> }
					{ ( ( requireAccept && accepted ) || !requireAccept ) && <>
						<div className="row">
							<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
								<FixedMobileInput
									id={ "mobile" }
									holder={ this }
									required={ true }
									label={ Strings.mobile_number }
									containerClassName={ "form-group field-style" }
									inputClassName={ "form-control ltr-text-field" }/>
							</div>
							{ !isInternal &&
								<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
									<PickerInput
										id={ "valuationType" }
										holder={ this }
										required={ true }
										label={ Strings.property_valuation_type }
										items={ state.valuationTypes }
										formatter={ ( item: Lookup ) => I8N.isRTL() ? item.NameAr : item.NameEn }
										toIdentifier={ ( item: Lookup ) => item.Identity + "" }
										onValueChange={ ( type?: Lookup ) => {
											this.setState( {
												showIncome: type?.Identity !== PropertyValuationForm.EVAL_TYPE_DUBAI_MUNICIPALITY,
											} );
										} }/>
								</div>
							}
						</div>
						{ state.showIncome && <RenderIncome holder={ this } state={ this.state }/> }
						{ state.property?.propertyTypeId === TabuPropertyType.LAND &&
							<React.Fragment>
								<RenderLand holder={ this } state={ this.state }/>
								<RenderTabuRequiredDocuments
									reqDocuments={ state.landReqDocuments }
									onDropRejected={ this.onDropRejected }
									onDrop={ this.onDrop }
									onDelete={ this.onDelete }/>
							</React.Fragment>
						}
						{ state.property?.propertyTypeId === TabuPropertyType.UNIT &&
							<RenderUnit holder={ this } state={ this.state }/> }
						{ state.property?.propertyTypeId === TabuPropertyType.VILLA &&
							<RenderVilla holder={ this } state={ this.state }/> }
					</> }
				</div>
			</div>
			{ ( ( requireAccept && accepted ) || !requireAccept ) &&
				<div className="row">
					<div className="col-md-12 mt-3">
						<button
							className="btn btn_1 btn_style1" type="submit" id="btnContinue"
							onClick={ this.onContinue }>{ Strings.continue }</button>
						<a className="ms-3" onClick={ this.onReset }>{ Strings.reset }</a>
					</div>
				</div> }

		</PageContent>;
	}

	openPT = ( e: React.MouseEvent ) => {
		e.preventDefault();
		OpenPTOffice();
	};

	private loadLookups() {
		showLoading();
		const { property, isInternal } = this.state;
		if ( isInternal ) {
			this.setState( { valuationTypes: IPVTypes } );
			this.loadPropertyValuationInfo();
		} else {
			let name = ( property!.propertyTypeId === TabuPropertyType.LAND ) ? TabuLookupName.LAND_VALUATION_TYPES :
				TabuLookupName.VALUATION_TYPES;
			getLookup( name, property!.propertyId ).then( TabuUtils.validateResponse )
			.then( ( data: Lookup[] ) => {
				this.setState( { valuationTypes: data } );
				this.loadPropertyValuationInfo();
			} ).catch( ( reason ) => {
				this.loadPropertyValuationInfo();
			} );
		}

	}

	private loadPropertyValuationInfo() {
		const property = this.state.property;
		const req: PropertyEvaluationInfoRequest = {
			ProcedureId: this.state.service.ID,
			PropertyWithTypeIds: [ {
				PropertyId: property!.propertyId,
				PropertyTypeId: property!.propertyTypeId,
			} ],
		};
		getPropertyEvaluationInfo( req ).then( TabuUtils.validateResponse ).then( ( info: PropertyEvaluationInfo ) => {
			this.evaluationInfo = info;
			let income = info.PropertyTotalIncome;
			let munNum = info.MunicipalityNumber;
			let buildArea = info.BuildupArea;
			let isIncomeValid = ( income !== undefined && income !== 0 );
			let isMunNumValid = ( munNum !== undefined && !StringUtil.isEmptyStringOrNull( munNum ) );
			let isBuiltUpValid = ( buildArea !== undefined && buildArea !== 0 );
			this.setState( {
				incomeDisable: isIncomeValid,
				income: isIncomeValid ? "" + income : "",
				dmNumDisable: isMunNumValid,
				dmNum: isMunNumValid ? munNum : "",
				builtUpDisable: isBuiltUpValid,
				builtUpArea: isBuiltUpValid ? "" + buildArea : "",
			} );
			this.loadRemainingLookups();
		} ).catch( ( reason ) => {
			hideLoading();
			showErrorMessage( reason.toString() );
		} );
	}

	private onDropRejected = () => {
		showErrorMessage( Strings.tabu_file_exceed_size );
	};

	private loadRemainingLookups() {
		const property = this.state.property;
		if ( property!.propertyTypeId === TabuPropertyType.LAND ) {
			getLookup( TabuLookupName.LAND_REQ_DOCUMENTS ).then( TabuUtils.validateResponse ).then( ( data: any[] ) => {
				hideLoading();
				this.setState( { landReqDocuments: data } );
			} ).catch( ( reason ) => {
				hideLoading();
			} );
		} else if ( property!.propertyTypeId === TabuPropertyType.UNIT ) {
			getLookup( TabuLookupName.MAP_EXCEPT_REASONS ).then( TabuUtils.validateResponse ).then( ( data: any[] ) => {
				hideLoading();
				this.setState( { mapExceptionReasons: data } );
			} ).catch( ( reason ) => {
				hideLoading();
			} );
		} else {
			hideLoading();
		}
	}

	private onDrop = ( doc: ReqTabuDoc, files: File[] ) => {
		if ( doc.MaxPages > 1 ) {
			if ( doc.docs && doc.docs.length > 0 ) {
				let maxAllowed = ( doc.MaxPages - doc.docs.length );
				if ( files.length > maxAllowed )
					files = files.splice( 0, maxAllowed );
			} else if ( files.length > doc.MaxPages ) {
				files = files.splice( 0, doc.MaxPages );
			}
		}
		files.forEach( ( file ) => {
			if ( file.type.startsWith( "image/" ) || file.type.startsWith( "application/pdf" ) ) {
				this.addFile( doc, file );
			}
		} );
	};

	private addFile( reqTabuDoc: ReqTabuDoc, file: File ) {
		const reader = new FileReader();
		reader.onload = () => {
			const binaryStr = reader.result;
			const tabuDoc: TabuDoc = {
				identity: reqTabuDoc.Identity,
				localData: binaryStr as string,
				fileName: file.name,
				fileSize: file.size,
				type: file.type,
			};
			if ( reqTabuDoc.MaxPages > 1 ) {
				if ( reqTabuDoc.docs == undefined ) {
					reqTabuDoc.docs = [];
				}
			} else {
				reqTabuDoc.docs = [];
			}
			reqTabuDoc.docs.push( tabuDoc );
			this.setState( {
				landReqDocuments: this.state.landReqDocuments.map( ( doc: ReqTabuDoc ) => {
					return ( reqTabuDoc.Identity === doc.Identity ) ? { ...doc, docs: reqTabuDoc.docs } : doc;
				} ),
			} );
		};
		reader.readAsDataURL( file );
	}

	private onDelete = ( identity: number, index: number ) => {
		this.setState( {
			landReqDocuments: this.state.landReqDocuments.map( ( reqTabuDoc: ReqTabuDoc ) => {
				if ( reqTabuDoc.Identity === identity ) {
					if ( reqTabuDoc.docs && reqTabuDoc.docs.length > 0 ) {
						reqTabuDoc.docs.splice( index, 1 );
					}
					return reqTabuDoc;
				}
				return reqTabuDoc;
			} ),
		} );
	};

	private onContinue = () => {
		if ( this.validateForm() ) {
			showLoading();
			if ( this.state.landReqDocuments && this.state.landReqDocuments.length > 0 ) {
				this.uploadDocuments( this.state.landReqDocuments ).then( ( docs: TabuDocumentRequest[] ) => {
					this.submitValuationCertificateRequest( docs );
				} ).catch( ( reason ) => {
					hideLoading();
					showErrorMessage( reason.toString() );
				} );
			} else
				this.submitValuationCertificateRequest( undefined );
		} else {
			showErrorMessage( Strings.mandatory_fields_note );
		}
	};

	private validateForm() {
		const state = this.state;
		let mobile = toMobileX( state.mobile );
		let isValid = ( mobile.length === 12 );
		isValid = state.valuationType !== undefined && isValid;
		if ( state.showIncome ) {
			isValid = !StringUtil.isEmptyStringOrNull( state.income ) && isValid;
			isValid = !StringUtil.isEmptyStringOrNull( state.expense ) && isValid;
			isValid = !StringUtil.isEmptyStringOrNull( state.netIncome ) && isValid;
		}
		if ( state.property!.propertyTypeId === TabuPropertyType.LAND ) {
			isValid = !StringUtil.isEmptyStringOrNull( state.landUsage ) && isValid;
			isValid = ( state.mapIssueDate !== undefined ) && isValid;
			isValid = !StringUtil.isEmptyStringOrNull( state.dmNum ) && isValid;
			if ( isValid ) {
				for ( const doc of state.landReqDocuments ) {
					if ( doc.IsRequired && ( doc.docs == undefined || doc.docs.length === 0 ) )
						isValid = false;
				}
			}
		} else if ( state.property!.propertyTypeId === TabuPropertyType.UNIT ) {
			isValid = !StringUtil.isEmptyStringOrNull( state.locOnFloor ) && isValid;
			isValid = !StringUtil.isEmptyStringOrNull( state.propertyAge ) && isValid;
			isValid = !StringUtil.isEmptyStringOrNull( state.propertyView ) && isValid;
			if ( state.mapException )
				isValid = state.mapExceptionReason !== undefined && isValid;
			else
				isValid = ( state.mapIssueDate !== undefined ) && isValid;
		} else if ( state.property!.propertyTypeId === TabuPropertyType.VILLA ) {
			isValid = !StringUtil.isEmptyStringOrNull( state.builtUpArea ) && state.builtUpArea !== "0" && isValid;
			isValid = !StringUtil.isEmptyStringOrNull( state.propertyAge ) && isValid;
			isValid = !StringUtil.isEmptyStringOrNull( state.landUsage ) && isValid;
			isValid = !StringUtil.isEmptyStringOrNull( state.noOfFloors ) && isValid;
			isValid = !StringUtil.isEmptyStringOrNull( state.noOfRooms ) && isValid;
			isValid = !StringUtil.isEmptyStringOrNull( state.insuranceValue ) && isValid;
		}
		return isValid;
	}

	private onReset = () => {
		let mobile = phoneNumber();
		if ( mobile && !StringUtil.isEmptyStringOrNull( mobile ) ) {
			mobile = toMobileX( mobile );
		}
		let income, munNum, buildArea;
		if ( this.evaluationInfo ) {
			income = this.evaluationInfo.PropertyTotalIncome;
			munNum = this.evaluationInfo.MunicipalityNumber;
			buildArea = this.evaluationInfo.BuildupArea;
		}
		let isIncomeValid = ( income !== undefined && income !== 0 );
		let isMunNumValid = ( munNum !== undefined && !StringUtil.isEmptyStringOrNull( munNum ) );
		let isBuiltUpValid = ( buildArea !== undefined && buildArea !== 0 );
		this.setState( {
			mobile: mobile,
			valuationType: undefined,
			showIncome: true,
			income: isIncomeValid ? "" + income : "",
			expense: "",
			netIncome: "",
			incomeDisable: isIncomeValid,
			landUsage: "",
			dmNum: isMunNumValid ? "" + munNum : "",
			dmNumDisable: isMunNumValid,
			landReqDocuments: this.state.landReqDocuments.map( ( reqDoc: ReqTabuDoc ) => {
				return { ...reqDoc, docs: undefined };
			} ),
			locOnFloor: "",
			propertyAge: "",
			propertyView: "",
			mapException: false,
			builtUpArea: isBuiltUpValid ? "" + buildArea : "",
			builtUpDisable: isBuiltUpValid,
			noOfFloors: "",
			insuranceValue: "",
			swimmingPool: false,
		} );
	};

	private submitValuationCertificateRequest( docs?: TabuDocumentRequest[] ) {
		const state = this.state;
		let req: PropertyEvaluationRequest | undefined;
		if ( state.property!.propertyTypeId === TabuPropertyType.LAND )
			req = this.getLandRequest( docs );
		else if ( state.property!.propertyTypeId === TabuPropertyType.UNIT )
			req = this.getUnitRequest();
		else if ( state.property!.propertyTypeId === TabuPropertyType.VILLA )
			req = this.getVillaRequest();
		if ( req ) {
			addAgentParticipantId( state.property, req );
			trackEventWithData( "TabuProcedure", "TrySubmit", req );
			const api = state.isInternal ? saveInternalPropertyEvaluation : savePropertyEvaluation;
			api( req ).then( TabuUtils.validateResponse ).then( ( resp: TabuProcedure ) => {
				hideLoading();
				trackEventWithData( "TabuProcedure", "SubmitSuccess", {
					number: resp.ProcedureNumber,
					year: resp.ProcedureYear, ...req
				} );
				this.navigateToVoucher( this.props, resp );
			} ).catch( ( reason ) => {
				hideLoading();
				showErrorMessage( reason.toString() );
				trackEventWithData( "TabuProcedure", "SubmitFail", { reason: reason.toString(), ...( req as PropertyEvaluationRequest ) } );
			} );
		}
	}

	private getLandRequest( docs?: TabuDocumentRequest[] ): PropertyEvaluationRequest {
		const state = this.state;
		return {
			PropertyWithTypeIds: [ {
				PropertyId: state.property!.propertyId,
				PropertyTypeId: state.property!.propertyTypeId,
			} ],
			ProcedureId: state.service.ID,
			ParticipantId: "" + getParticipantID(),
			ApplicantMobile: toMobileX( state.mobile ),
			EvaluationTypeId: state.valuationType!.Identity,
			PropertyTotalIncome: !StringUtil.isEmptyStringOrNull( state.income ) ? state.income : "0",
			PropertyTotalExpenses: !StringUtil.isEmptyStringOrNull( state.expense ) ? state.expense : "0",
			PropertyNetIncome: !StringUtil.isEmptyStringOrNull( state.netIncome ) ? state.netIncome : "0",
			MunicipalityLandUsage: state.landUsage,
			MuncipalityMapIssueDate: state.mapIssueDate ? DateUtil.toServerDate( state.mapIssueDate ) : "",
			MunicipalityNumber: state.dmNum,
			Documents: docs,
			Identity: 0,
		};
	}

	private getUnitRequest(): PropertyEvaluationRequest {
		const state = this.state;
		return {
			PropertyWithTypeIds: [ {
				PropertyId: state.property!.propertyId,
				PropertyTypeId: state.property!.propertyTypeId,
			} ],
			ProcedureId: state.service.ID,
			ParticipantId: "" + getParticipantID(),
			ApplicantMobile: toMobileX( state.mobile ),
			EvaluationTypeId: state.valuationType!.Identity,
			PropertyTotalIncome: !StringUtil.isEmptyStringOrNull( state.income ) ? state.income : "0",
			PropertyTotalExpenses: !StringUtil.isEmptyStringOrNull( state.expense ) ? state.expense : "0",
			PropertyNetIncome: !StringUtil.isEmptyStringOrNull( state.netIncome ) ? state.netIncome : "0",
			PropertyAge: parseInt( state.propertyAge ),
			LocationOnFloor: state.locOnFloor,
			SideInterface: state.propertyView,
			DldMapNotAvailable: state.mapException,
			DldMapExceptionReasonId: state.mapException ? state.mapExceptionReason!.Identity : 0,
			DldMapIssueDate: !state.mapException ? DateUtil.toServerDate( state.mapIssueDate! ) : undefined,
			Identity: 0,
		};
	}

	private getVillaRequest(): PropertyEvaluationRequest {
		const state = this.state;
		return {
			PropertyWithTypeIds: [ {
				PropertyId: state.property!.propertyId,
				PropertyTypeId: state.property!.propertyTypeId,
			} ],
			ProcedureId: state.service.ID,
			ParticipantId: "" + getParticipantID(),
			ApplicantMobile: toMobileX( state.mobile ),
			EvaluationTypeId: state.valuationType!.Identity,
			PropertyTotalIncome: !StringUtil.isEmptyStringOrNull( state.income ) ? state.income : "0",
			PropertyTotalExpenses: !StringUtil.isEmptyStringOrNull( state.expense ) ? state.expense : "0",
			PropertyNetIncome: !StringUtil.isEmptyStringOrNull( state.netIncome ) ? state.netIncome : "0",
			BuildupArea: state.builtUpArea,
			PropertyAge: parseInt( state.propertyAge ),
			LandUsesAccordingToMunicipalityMap: state.landUsage,
			NumberOfFloors: state.noOfFloors,
			NumberOfRooms: state.noOfRooms,
			InsuranceValue: state.insuranceValue,
			HasSwimmingPool: state.swimmingPool,
			Identity: 0,
		};
	}

	private savePropertyEvaluation( req: PropertyEvaluationRequest ) {
		savePropertyEvaluation( req ).then( TabuUtils.validateResponse ).then( ( resp: TabuProcedure ) => {
			hideLoading();
			this.navigateToVoucher( this.props, resp );
		} ).catch( ( reason ) => {
			hideLoading();
			showErrorMessage( reason.toString() );
		} );
	}
}

function RenderIncome( props: { holder: any, state: any } ) {
	const { holder, state } = props;
	return <React.Fragment>
		<div className="row">
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<CurrencyInput
					id={ "income" }
					holder={ holder }
					required={ true }
					label={ Strings.property_income_value }
					containerClassName={ "form-group field-style" }
					inputClassName={ "form-control" }
					disabled={ state.incomeDisable }
					onValueChange={ ( text ) => {
						holder.setState( {
							netIncome: PropertyValuationForm.computeNetIncome( text, state.expense ),
						} );
					} }
					currencyType={ Strings.aed }/>
			</div>
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<CurrencyInput
					id={ "expense" }
					holder={ holder }
					required={ true }
					label={ Strings.property_expenses }
					containerClassName={ "form-group field-style" }
					inputClassName={ "form-control" }
					onValueChange={ ( text ) => {
						holder.setState( {
							netIncome: PropertyValuationForm.computeNetIncome( state.income, text ),
						} );
					} }
					currencyType={ Strings.aed }/>
			</div>
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<CurrencyInput
					id={ "netIncome" }
					holder={ holder }
					required={ true }
					label={ Strings.property_net_income }
					containerClassName={ "form-group field-style" }
					inputClassName={ "form-control" }
					disabled={ true }
					currencyType={ Strings.aed }/>
			</div>
		</div>
	</React.Fragment>;
}

function RenderLand( props: { holder: any, state: any } ) {
	const { holder, state } = props;
	let now = moment();
	let pMapIssueMaxDate = now.toDate();
	let pMapIssueMinDate = now.subtract( 1, "year" ).toDate();
	return <React.Fragment>
		<div className="row">
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<TextInputField
					id={ "landUsage" }
					holder={ holder }
					required={ true }
					label={ Strings.municipality_land_usage }
					containerClassName={ "form-group field-style" }
					inputClassName={ "form-control" }/>
			</div>
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<JqDatePicker
					id={ "mapIssueDate" }
					required={ true }
					label={ Strings.municipality_map_issue_data }
					containerClassName={ "form-group mb-4 position-relative field-style" }
					inputClassName={ "form-control dailyDate" }
					value={ state.mapIssueDate }
					onDateSet={ ( d ) => {
						holder.setState( { mapIssueDate: d } );
					} }
					opts={ {
						changeMonth: true,
						changeYear: true,
						dateFormat: "dd/mm/yy",
						maxDate: pMapIssueMaxDate,
						minDate: pMapIssueMinDate,
						defaultDate: state.mapIssueDate,
						showOn: "both",
						buttonText: "",
						showAnim: "slideDown",
					} }
					placeholder={ " " }/>
			</div>
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<TextInputField
					id={ "dmNum" }
					holder={ holder }
					required={ true }
					label={ Strings.municipality_number }
					containerClassName={ "form-group field-style" }
					inputClassName={ "form-control" }
					disabled={ state.dmNumDisable }/>
			</div>
		</div>
	</React.Fragment>;
}

function RenderUnit( props: { holder: any, state: any } ) {
	const { holder, state } = props;
	return <React.Fragment>
		<div className="row">
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<TextInputField
					id={ "locOnFloor" }
					holder={ holder }
					required={ true }
					label={ Strings.property_location_on_floor }
					containerClassName={ "form-group field-style" }
					inputClassName={ "form-control" }/>
			</div>
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<TextInputField
					id={ "propertyAge" }
					holder={ holder }
					required={ true }
					label={ Strings.property_age }
					containerClassName={ "form-group field-style" }
					inputClassName={ "form-control" }
					type={ "text" }
					converter={ NumberConverter }/>
			</div>
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<TextInputField
					id={ "propertyView" }
					holder={ holder }
					label={ Strings.property_view }
					required={ true }
					containerClassName={ "form-group field-style" }
					inputClassName={ "form-control" }/>
			</div>
		</div>
		<div className="row">
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<CheckBoxField
					id={ "mapException" }
					holder={ holder }
					label={ Strings.map_not_available }
					labelClassName={ "custom-control-label" }
					required={ true }/>
			</div>
		</div>
		{ !state.mapException && <div className="row">
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<JqDatePicker
					id={ "mapIssueDate" }
					required={ true }
					label={ Strings.dld_map_issue_data }
					containerClassName={ "form-group mb-4 position-relative field-style" }
					inputClassName={ "form-control dailyDate" }
					value={ state.mapIssueDate }
					onDateSet={ ( d ) => {
						holder.setState( { mapIssueDate: d } );
					} }
					opts={ {
						changeMonth: true,
						changeYear: true,
						dateFormat: "dd/mm/yy",
						maxDate: new Date(),
						defaultDate: state.mapIssueDate,
						showOn: "both",
						buttonText: "",
						showAnim: "slideDown",
					} }
					placeholder={ " " }/>
			</div>
		</div>
		}
		{ state.mapException && <div className="row">
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<PickerInput
					id={ "mapExceptionReason" }
					holder={ holder }
					required={ true }
					label={ Strings.map_exception_reason }
					items={ state.mapExceptionReasons }
					formatter={ ( item: Lookup ) => I8N.isRTL() ? item.NameAr : item.NameEn }
					toIdentifier={ ( item: Lookup ) => item.Identity + "" }/>
			</div>
		</div> }
	</React.Fragment>;
}

function RenderVilla( props: { holder: any, state: any } ) {
	const { holder, state } = props;
	return <React.Fragment>
		<div className="row">
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<TextInputField
					id={ "builtUpArea" }
					holder={ holder }
					required={ true }
					label={ Strings.built_up_area }
					containerClassName={ "form-group field-style" }
					inputClassName={ "form-control" }
					type={ "text" }
					converter={ NumberDecimalConverter }
					cursorPositioner={ CursorPositioner }
					disabled={ state.builtUpDisable }/>
			</div>
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<TextInputField
					id={ "propertyAge" }
					holder={ holder }
					required={ true }
					label={ Strings.property_age }
					containerClassName={ "form-group field-style" }
					inputClassName={ "form-control" }
					type={ "text" }
					converter={ NumberConverter }/>
			</div>
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<TextInputField
					id={ "landUsage" }
					holder={ holder }
					required={ true }
					label={ Strings.municipality_land_usage }
					containerClassName={ "form-group field-style" }
					inputClassName={ "form-control" }/>
			</div>
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<TextInputField
					id={ "noOfFloors" }
					holder={ holder }
					required={ true }
					label={ Strings.no_of_floors }
					containerClassName={ "form-group field-style" }
					inputClassName={ "form-control" }
					type={ "text" }
					converter={ NumberConverter }/>
			</div>
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<TextInputField
					id={ "noOfRooms" }
					holder={ holder }
					required={ true }
					label={ Strings.no_of_rooms }
					containerClassName={ "form-group field-style" }
					inputClassName={ "form-control" }
					type={ "text" }
					converter={ NumberConverter }/>
			</div>
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<CurrencyInput
					id={ "insuranceValue" }
					holder={ holder }
					required={ true }
					label={ Strings.insurance_value }
					containerClassName={ "form-group field-style" }
					inputClassName={ "form-control" }
					currencyType={ Strings.aed }/>
			</div>
			<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
				<CheckBoxField
					id={ "swimmingPool" }
					holder={ holder }
					label={ Strings.swimming_pool }
					labelClassName={ "custom-control-label" }
					required={ true }/>
			</div>
		</div>
	</React.Fragment>;
}

export function OpenPTOffice() {
	window.open( getSiteLocaleRootUrl() + "eservices/search-for-property-service-trustees-centers/properties-trustee/", "_pt_offices" );
}

export function LandConfirmation() {
	const popUp = useRef<Popup>( null );
	const hideMe = () => {
		popUp.current?.dissmiss();
	};
	return <>
		<Popup
			ref={ popUp }
			title={ Strings.information }
			showIcon={ false }
			clickOutsideToDismiss={ false }
			autoOpenMount={ true } id={ "popup-empty-land-only" }
			iconClass={ "icon-info-bell" }
			showCloseButton={ false }
			modalMainContainer={ "modal fade" }
		>
			<div
				className="d-flex align-items-center justify-content-center flex-column py-4 valuation-empty-land">
				<div className="icon-info-bell"/>
				<h4>{ Strings.information }</h4>
				<p className="font-size14 mb-4">{ Strings.land_eval_popup_info }</p>
				<button
					type="button" className="btn btn_1 btn_style1"
					onClick={ hideMe }>{ Strings.i_understand }</button>
			</div>
		</Popup>
	</>;
}

export default PropertyValuationForm;
